import { Chat, Button, FullLoader, IconButton } from '@sc/shared-ui';
import {
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useBreadCrumbs } from '../providers/breadcrumbs';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import {
  GQL_FRAGMENT_PROJECT,
  GQL_MUTATION_GENERATE_PROJECT_QUESTIONS,
  GQL_MUTATION_UPDATE_PROJECT,
  GQL_QUERY_ME,
  GQL_QUERY_READ_PROJECT,
} from '../utility/queries';
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { notify } from '../utility/notify';
import { ManageSources } from '../modals/ManageSources';
import {
  getFragmentData,
  ProjectState,
  UserCompanyLimitation,
} from '@sc/graphql';
import { CatchBoundary, Link, useRouter } from '@tanstack/react-router';
import { useAlerts } from '../providers/alerts';
import env from '../environment';
import { PersonalityModal } from '../modals/PersonalityModal';
import { ProjectSettingsModal } from '../modals/ProjectSettingsModal';
import { ProjectPublishedModal } from '../modals/ProjectPublishedModal';
import { getProjectUrl } from '../utility/projectUrl';
import { useUser } from '../providers/users';
import { ProjectSources } from '../components/ProjectSources';
import { ProjectThemePicker } from '../components/ProjectThemePicker';
import { EditableTextField } from '../components/EditableTextField';
import { ThemeFormModal } from '../modals/ThemeFormModal';
import { track } from '../utility/analytics';

interface Props {
  uuid: string;
  queryUuid?: string | undefined;
  showHistory?: boolean | undefined;
  openModal?: string | undefined;
}

export const Project = ({
  uuid,
  queryUuid,
  showHistory,
  openModal,
}: Props): ReactElement => {
  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const { user } = useUser();

  const client = useApolloClient();

  // An unauthorised send can mean the company just ran out of queries —
  // refresh the user so limitations swap the chat for the billing warning.
  const onChatError = useCallback(
    (error: unknown) => {
      if (
        CombinedGraphQLErrors.is(error) &&
        error.errors.some((e) => e.extensions?.code === 'UNAUTHORISED')
      ) {
        void client.query({ query: GQL_QUERY_ME, fetchPolicy: 'network-only' });
      }

      onError(error);
    },
    [client, onError]
  );

  const [isEditThemeOpen, setIsEditThemeOpen] = useState(false);

  const { data, stopPolling, startPolling } = useQuery(GQL_QUERY_READ_PROJECT, {
    variables: {
      uuid,
    },
    context: {
      isBatched: true,
    },
  });

  const project = getFragmentData(GQL_FRAGMENT_PROJECT, data?.readProject);

  const [updateProject] = useMutation(GQL_MUTATION_UPDATE_PROJECT, {
    onCompleted: () => notify.success('Updated knowledge base.'),
  });

  const [generateQuestions] = useMutation(
    GQL_MUTATION_GENERATE_PROJECT_QUESTIONS
  );

  const { setCrumbs } = useBreadCrumbs();

  useEffect(() => {
    if (project) {
      setCrumbs([
        {
          label: 'Knowledge bases',
          from: '/kb',
        },
        {
          label: project.name,
          from: '/kb/$uuid',
          params: {
            uuid,
          },
        },
      ]);
    }
    return () => {
      setCrumbs([]);
    };
  }, [setCrumbs, uuid, project]);

  const isGeneratingQuestions =
    !!project &&
    !!project.processedAt &&
    project.questions.length === 0 &&
    project.sourceCount > 0;

  useEffect(() => {
    if (
      (project?.processedPc === 100 || project?.sourceCount === 0) &&
      !isGeneratingQuestions
    ) {
      stopPolling();
    } else {
      startPolling(1000);
    }
    return () => {
      stopPolling();
    };
  }, [project, isGeneratingQuestions, startPolling, stopPolling]);

  const router = useRouter();

  const onOpenModal = useCallback(
    (modal: string | undefined) => {
      if (project?.uuid) {
        void router.navigate({
          to: queryUuid ? '/kb/$uuid/$queryUuid' : '/kb/$uuid',
          params: {
            queryUuid,
            uuid: project.uuid,
          },
          search: {
            modal,
          },
          replace: true,
        });
      }
    },

    [project?.uuid, queryUuid, router]
  );

  const userCannotSendQueryMessages = user.company.limitations.includes(
    UserCompanyLimitation.cannotCreateQueryMessage
  );

  if (!project) return <FullLoader className="size-8" />;

  return (
    <>
      <div className="flex grow overflow-hidden">
        <div className="flex flex-col w-full max-w-80 grow shrink-0 border-r border-grey-t-700 overflow-y-auto">
          <div className="py-3.5 px-2">
            <EditableTextField
              onChange={(value) =>
                void updateProject({
                  variables: {
                    input: {
                      uuid,
                      name: value,
                      customDomainUuids: undefined,
                      rules: undefined,
                      state: undefined,
                      themeUuid: undefined,
                      title: undefined,
                      subtitle: undefined,
                      questions: undefined,
                    },
                  },
                })
              }
              value={project.name}
            />
          </div>
          <div className="grow mb-5">
            {project.sourceCount === 0 ? (
              <div className="flex items-center space-x-2 mx-5">
                <ExclamationTriangleIcon className="text-grey-400 size-4" />
                <span className="text-sm">No sources configured</span>
              </div>
            ) : (
              <Suspense fallback={<FullLoader className="size-5" />}>
                <ProjectSources state={project.state} projectUuid={uuid} />
              </Suspense>
            )}
          </div>
          <div className="mx-5 mb-5 space-y-2">
            <div className="flex items-end gap-2">
              <div className="grow">
                <Suspense fallback={<FullLoader className="size-5" />}>
                  <ProjectThemePicker
                    projectUuid={uuid}
                    currentThemeName={project.theme?.name ?? null}
                  />
                </Suspense>
              </div>
              {project.theme && (
                <IconButton
                  Icon={PencilSquareIcon}
                  type="tertiary"
                  size="medium"
                  tooltip="Edit theme"
                  onClick={() => setIsEditThemeOpen(true)}
                />
              )}
            </div>
            <Button
              button={{
                text: 'Configure sources',
                size: 'medium',
                type: 'secondary',
                disabled: project.processedPc < 100 && project.sourceCount > 0,
              }}
              link={{
                to: '.',
                hash: 'sources-google-drive',
              }}
            />
            <Button
              button={{
                text: 'Edit settings',
                size: 'medium',
                type: 'low-priority',
                onClick: () => onOpenModal('settings'),
              }}
            />
            {project.state !== ProjectState.published && (
              <Button
                button={{
                  text: 'Publish',
                  size: 'medium',
                  type: 'primary',
                  onClick: () => onOpenModal('settings-publish'),
                }}
              />
            )}
          </div>
        </div>
        <CatchBoundary
          getResetKey={() => 'project-reset'}
          onCatch={(error) => console.error(error)}
          errorComponent={() => <>Something went wong</>}
        >
          {userCannotSendQueryMessages ? (
            <Link
              to="/billing"
              className="cursor-pointer border border-red-400 bg-red-1000 mt-5 rounded-xs p-1 flex mx-5 h-fit"
            >
              <div className="p-3 flex">
                <ExclamationCircleIcon className="size-5 text-red-400" />
                <span className="ml-3 inline-block text-sm">
                  No queries remaining - you won't be able to send messages
                  until your plan renews or you upgrade. <u>Billing</u>
                </span>
              </div>
            </Link>
          ) : (
            <Chat
              chatsUrl={env.chatsUrl}
              onShowHistory={(showHistory) =>
                void router.navigate({
                  search: {
                    // @ts-expect-error not fighting with TS types
                    history: showHistory ? true : undefined,
                  },
                })
              }
              showHistory={showHistory}
              messageRetrievalEndpoint={env.messageRetrievalApiUrl}
              navigateToQuery={(uuid) =>
                void router.navigate({
                  to: '/kb/$uuid/$queryUuid',
                  params: {
                    queryUuid: uuid,
                    uuid: project.uuid,
                  },
                })
              }
              onNewChat={() =>
                void router.navigate({
                  to: '/kb/$uuid',
                  params: {
                    uuid: project.uuid,
                  },
                })
              }
              domains={project.domains.map((d) => d.url)}
              onError={onChatError}
              domainPrefix={project.domainPrefix}
              // sessionId={user.uuid} // file upload temporarily hidden
              queryUuid={queryUuid}
              projectUuid={project.uuid}
              state={project.state}
              sourceCount={project.sourceCount}
              progress={project.processedPc}
              theme={project.theme}
              title={project.title}
              subtitle={project.subtitle}
              questions={project.questions.map((q) => q.question)}
              onGenerateQuestions={async () => {
                track('Project Questions Generated', {
                  projectUuid: project.uuid,
                });

                return generateQuestions({
                  variables: { projectUuid: project.uuid },
                });
              }}
              isGeneratingQuestions={isGeneratingQuestions}
              onEditQuestions={() => onOpenModal('settings-questions')}
              onShowRateLimits={() => onOpenModal('settings-limits')}
              onMessageSent={({
                isFirstMessage,
                queryUuid: sentQueryUuid,
                question,
              }) => {
                if (isFirstMessage) {
                  track('Query Created', {
                    projectUuid: project.uuid,
                    queryUuid: sentQueryUuid,
                    ...(question ? { question } : {}),
                  });
                } else {
                  track('Query Message Created', {
                    projectUuid: project.uuid,
                    queryUuid: sentQueryUuid,
                  });
                }
              }}
            />
          )}
        </CatchBoundary>
      </div>
      <ManageSources uuid={uuid} queryUuid={queryUuid} />

      <PersonalityModal
        open={openModal === 'personality'}
        onClose={() => onOpenModal(undefined)}
      />
      <ProjectSettingsModal
        open={!!openModal?.startsWith('settings')}
        project={project}
        onClose={() => onOpenModal(undefined)}
        onPublished={() => onOpenModal('published')}
      />
      <ProjectPublishedModal
        open={openModal === 'published'}
        url={getProjectUrl(project, env.chatsUrl)}
        onClose={() => onOpenModal(undefined)}
      />
      <ThemeFormModal
        key={`edit-${project.theme?.uuid}`}
        open={isEditThemeOpen && !!project.theme}
        mode="edit"
        uuid={project.theme?.uuid}
        onClose={() => setIsEditThemeOpen(false)}
      />
    </>
  );
};
